/**
 * Tab 3: Fraud Risk
 * 
 * Features:
 * - Fraud Triangle cards (Incentives, Opportunities, Rationalizations)
 * - Compliance Purpose table (formerly Specific Fraud Risk Factors)
 * - Fraud Risk Conclusion (synced with Tab 1 - Fraud Risk row)
 * 
 * User Story 1: Fraud Triangle Documentation and Autosave
 * - Three editable text cards
 * - Autosave on blur
 * - Read-only display when not focused
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Input, Dropdown, message, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import { RootState, AppDispatch } from '../../../../store';
import { useAssessmentDetails } from '../../../../hooks/useAssessmentDetails';
import {
  setAssessmentDetails,
  setCompliancePurposes,
} from '../../../../store/slices/riskAssessmentSlice';
import {
  normalizeRiskRanking,
  updateIncentives,
  updateOpportunities,
  updateRationalizations,
  getCompliancePurposes,
  createCompliancePurpose,
  updateCompliancePurpose,
  deleteCompliancePurpose,
  parseCompliancePurposes,
  type CompliancePurpose,
  denormalizeRiskRanking,
  updateFraudRisk,
} from '../../../../services/riskAssessmentService';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Page,
} from '@syncfusion/ej2-react-grids';
import EditIcon from '../../../../assets/icons/ic-edit.svg?react';
import TrashIcon from '../../../../assets/icons/ic-trash.svg?react';
import ArrowUpIcon from '../../../../assets/icons/ic-arrow-up.svg?react';
import ArrowDownIcon from '../../../../assets/icons/ic-arrow-down.svg?react';
import ConfirmModal from '../../../../components/common/ConfirmModal';
import CompliancePurposeItemDrawer, { CompliancePurposeFormData } from './CompliancePurposeItemDrawer';
import QualitativeItemDrawer, { QualitativeFactorFormData } from '../tab1/QualitativeItemDrawer';
import warningIcon from '../../../../assets/icons/ic-warning.svg';
import { RiskRanking } from '../../../../services/types';

const { TextArea } = Input;

// ========== STYLED COMPONENTS ==========

const TabContent = styled.div`
  min-height: 400px;
`;

const SectionTitle = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 18px;
  color: #1F2937;
  margin-bottom: 20px;
`;

const FraudTriangleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 40px;
`;

const FraudCard = styled.div<{ $isEditing: boolean }>`
  background: white;
  border: 1px solid ${props => props.$isEditing ? '#2563EB' : 'transparent'};
  border-radius: 8px;
  box-shadow: ${props => props.$isEditing ? '0 0 0 3px rgba(37, 99, 235, 0.1)' : '0 1px 3px rgba(0, 0, 0, 0.1)'};
  transition: all 0.2s;
  cursor: ${props => props.$isEditing ? 'text' : 'pointer'};
  
  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const CardHeader = styled.div`
  padding: 16px 20px;
`;

const CardTitle = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  color: #1F2937;
`;

const CardBody = styled.div`
  padding: 0px 20px 16px 20px;
  min-height: 180px;
`;

const ReadOnlyText = styled.div<{ $disabled?: boolean }>`
  font-family: Inter;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  color: #6B7280;
  white-space: pre-wrap;
  word-wrap: break-word;
  
  &:empty::before {
    content: '${props => props.$disabled ? 'No content' : 'Click to add content...'}';
    color: #9CA3AF;
    font-style: italic;
  }
`;

const StyledTextArea = styled(TextArea)`
  font-family: Inter;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  color: #1F2937;
  border: none;
  padding: 0;
  resize: none;
  box-shadow: none !important;
  
  &:focus {
    border: none;
    box-shadow: none;
  }
  
  &::placeholder {
  color: #9CA3AF;
    font-style: italic;
  }
`;

// Compliance Purpose Section
const CompliancePurposeSection = styled.div`
  margin-top: 40px;
  background: #E0E7FF;
  border-radius: 6px;
`;

const CompliancePurposeTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 16px 0 16px;
  margin-bottom: 12px;
`;

const CompliancePurposeTitle = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  color: #1F2937;
`;

const CompliancePurposeDescription = styled.div`
  font-family: Inter;
  font-weight: 400;
  font-size: 14px;
  color: #6B7280;
  margin-bottom: 20px;
  padding: 0 16px;
`;

const CompliancePurposeContent = styled.div`
  padding: 0px 6px 2px 6px;
`;

// Loading State Components
const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #E5E7EB;
    border-top-color: #2563EB;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  .text {
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;
    color: #6B7280;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// Grid Components
const GridWrapper = styled.div`
  background: #E0E7FF;
  border-radius: 6px;
  border: 1px solid #E0E7FF;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);  
  overflow: hidden;
  padding: 0 0 0px 2px;

  .e-grid {
    border: none;
  }

  .e-grid .e-headercell {
    background: #F8FAFC;
    font-family: Inter;
    font-weight: 500;
    font-size: 10px;
    color: #6B7280;
    padding: 6px 8px;
    border-color: #E5E7EB;
    border-right: 1px solid #E5E7EB;
  }

  .e-grid .e-headercell:last-child {
    border-right: none;
  }

  .e-grid .e-rowcell {
    border-color: #E5E7EB;
    border-right: 1px solid #E5E7EB;
    padding: 12px 8px;
    font-size: 12px;
    color: #1F2937;
    white-space: normal;
    word-break: break-word;
    line-height: 1.5;
    vertical-align: top;
  }

  .e-grid .e-rowcell:last-child {
    border-right: none;
  }

  .e-grid .e-emptyrow {
    display: none;
  }

  /* Prevent dark hover/active state */
  .e-grid .e-row.e-active .e-rowcell,
  .e-grid .e-row.e-selectionbackground .e-rowcell,
  .e-grid .e-row.e-active,
  .e-grid .e-row.e-selectionbackground {
    background: transparent !important;
  }
`;

const ActionButton = styled.button`
  background: #F8FAFC;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  padding: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #6B7280;
  transition: all 0.3s;

  &:hover {
    color: #1F2937;
    background: #EFF6FF;
  }

  .anticon {
    font-size: 20px;
  }
`;

const MenuItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    flex-shrink: 0;
  }
`;

const MenuItemText = styled.span<{ $danger?: boolean }>`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.005em;
  color: ${props => props.$danger ? '#EF4444' : '#1F2937'};
`;

const IndexCell = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 0 12px;
  border: 1px solid #E5E7EB;
  border-radius: 14px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #6B7280;
`;

const ObjectivesCell = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  overflow: hidden;
  max-height: 64px;
`;

const ObjectiveGroupTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: #EFF6FF;
  color: #1E40AF;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover {
    background: #DBEAFE;
  }
`;

const ObjectiveGroupProcessCode = styled.span`
  font-weight: 600;
  margin-right: 2px;
`;

const ObjectiveGroupNumbers = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
`;

const ObjectiveGroupNumberCircle = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 4px;
  background: transparent;
  color: #2563EB;
  border: 1px solid #D1D5DB;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 600;
`;

const ControlChip = styled.span<{ $clickable?: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  background: #EFF6FF;
  color: #1E40AF;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  margin-right: 4px;
  margin-bottom: 4px;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  transition: all 0.2s;
  
  &:hover {
    ${props => props.$clickable && `
      background: #DBEAFE;
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(30, 64, 175, 0.15);
    `}
  }
`;

// Tooltip components for multiple controls
const TooltipTitle = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #1F2937;
  text-align: center;
  margin-bottom: 12px;
`;

const TooltipList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TooltipItem = styled.div`
  padding: 8px 12px;
  background: #EFF6FF;
  border-radius: 6px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #2563EB;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: #DBEAFE;
    transform: translateX(2px);
  }
`;

// Fraud Risk Conclusion Row (similar to SubTotalRow in ProcessHierarchyPage)
const FraudRiskConclusionRow = styled.div`
  display: grid;
  grid-template-columns: 68px auto 240px 300px 80px;
  align-items: center;
  background: white;
  border-radius: 0 0 6px 6px;
  border-top: 2px solid #E5E7EB;
  height: auto;
  margin-top: 0px;
`;

const FraudRiskConclusionCell = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  padding: 8px;
  border-left: 1px solid #E5E7EB;
`;

const ConclusionLabel = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  color: #1F2937;
`;

const ConclusionDescription = styled.div`
  font-family: Inter;
  font-weight: 400;
  font-size: 13px;
  color: #6B7280;
  line-height: 20px;
`;

const RiskChip = styled.span<{ $risk: string }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  background: white;

  ${props => {
    switch (props.$risk) {
      case 'Med':
        return `
          border: 1px solid #FACC15;
          color: #FACC15;
        `;
      case 'Low':
        return `
          border: 1px solid #2DD4BF;
          color: #2DD4BF;
        `;
      case 'High':
        return `
          border: 1px solid #EF4444;
          color: #EF4444;
        `;
      case 'Unranked':
      default:
        return `
          border: 1px solid #60A5FA;
          color: #60A5FA;
        `;
    }
  }}

  &:hover {
    opacity: 0.8;
  }

  &::before {
    content: '●';
    margin-right: 6px;
    font-size: 10px;
  }
`;

// ========== TYPES ==========

interface FraudTriangleData {
  incentives: string;
  opportunities: string;
  rationalizations: string;
}

type FraudCardType = 'incentives' | 'opportunities' | 'rationalizations';

// Fraud Risk Conclusion interface (sync with Tab 1 - Fraud Risk fixed row)
interface FraudRiskConclusion {
  id: string; // Fixed id for sync with Tab 1
  riskRanking: RiskRanking;
  customComment: string;
}

// ========== COMPONENT ==========

const Tab3FraudRisk: React.FC = () => {
  const { id: assessmentId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { isAdmin } = useSelector((state: RootState) => state.auth);
  
  // Use assessment details hook for refetching details
  const { isLoading: isLoadingDetails, refetchDetails } = useAssessmentDetails(assessmentId);
  
  // Get data from Redux
  const { currentAssessmentDetails, compliancePurposes, cycles: riskAssessmentCycles } = useSelector((state: RootState) => state.riskAssessment);

  // Fraud Triangle data state (synced with Redux)
  const [fraudTriangle, setFraudTriangle] = useState<FraudTriangleData>({
    incentives: '',
    opportunities: '',
    rationalizations: '',
  });

  // Sync Fraud Triangle data from Redux
  useEffect(() => {
    if (currentAssessmentDetails) {
      setFraudTriangle({
        incentives: currentAssessmentDetails.incentives || '',
        opportunities: currentAssessmentDetails.opportunities || '',
        rationalizations: currentAssessmentDetails.rationalizations || '',
      });
    }
  }, [currentAssessmentDetails]);

  // Edit mode states for each card
  const [editingCard, setEditingCard] = useState<FraudCardType | null>(null);
  const { selectedReport } = useSelector((state: RootState) => state.annualReport);

  // Temporary text for editing (before autosave)
  const [tempText, setTempText] = useState<string>('');

  // Ref for textarea to set cursor position
  const textareaRef = useRef<any>(null);

  // Loading state for compliance purposes
  const [isLoadingCompliancePurposes, setIsLoadingCompliancePurposes] = useState(false);

  // Ref to prevent duplicate fetches
  const hasFetchedCompliancePurposesRef = useRef(false);
  const isFetchingCompliancePurposesRef = useRef(false);
  const currentAssessmentIdRef = useRef<string | undefined>(undefined);

  // Fraud Risk Conclusion state (synced with Redux - Tab 1 Fraud Risk row)
  const fraudRiskConclusion: FraudRiskConclusion = {
    id: 'qf-fraud-risk', // Same id as Tab 1 fixed row for sync
    riskRanking: normalizeRiskRanking(currentAssessmentDetails?.fraudRiskRanking) || 'Unranked' as RiskRanking,
    customComment: currentAssessmentDetails?.fraudRiskDescription || '',
  };

  // Delete modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<CompliancePurpose | null>(null);
  const [isDeletingItem, setIsDeletingItem] = useState(false);

  // Drawer states
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'add' | 'edit'>('add');
  const [selectedItemForEdit, setSelectedItemForEdit] = useState<CompliancePurpose | null>(null);
  const [insertItemAtIndex, setInsertItemAtIndex] = useState<number | null>(null);

  // Conclusion Drawer states
  const [isConclusionDrawerOpen, setIsConclusionDrawerOpen] = useState(false);


  // ==================== DATA FETCHING ====================

  /**
   * Fetch compliance purposes from API and store in Redux
   */
  const fetchCompliancePurposes = useCallback(async (silent = false) => {
    if (!assessmentId) {
      console.warn('⚠️ Cannot fetch compliance purposes: assessmentId is undefined');
      return;
    }

    if (!silent) {
      setIsLoadingCompliancePurposes(true);
    }

    try {
      // console.log('🔄 Fetching compliance purposes for:', assessmentId);
      const response = await getCompliancePurposes(assessmentId, {
        skip: 0,
        take: 1000,
        requiresCounts: true,
      });

      const parsed = parseCompliancePurposes(response.result || []);
      dispatch(setCompliancePurposes(parsed));
      // console.log(`✅ Loaded ${parsed.length} compliance purposes`);
    } catch (err: any) {
      console.error('❌ Failed to fetch compliance purposes:', err);
      if (!silent) {
        message.error(err?.message || 'Failed to load compliance purposes');
      }
    } finally {
      if (!silent) {
        setIsLoadingCompliancePurposes(false);
      }
    }
  }, [assessmentId, dispatch]);

  useEffect(() => {
    // console.log('🔄 Compliance purposes:', compliancePurposes.length);
  }, [compliancePurposes, selectedReport?.annualReportId]);

  /**
   * Fetch compliance purposes when assessmentId or currentAssessmentDetails changes
   * Also refetch when compliancePurposes is cleared (e.g., after objective deletion in Tab 2)
   */
  useEffect(() => {
    // console.log('🔄 Compliance purposes:', compliancePurposes.length);
    // Wait for currentAssessmentDetails to be available
    if (!assessmentId || !currentAssessmentDetails) {
      return;
    }

    // Check if we need to fetch (new assessmentId or first time)
    const isNewAssessment = currentAssessmentIdRef.current !== assessmentId;
    
    if (isNewAssessment) {
      // console.log(`🆕 Compliance Purposes: Assessment ID changed: ${currentAssessmentIdRef.current} → ${assessmentId}`);
      hasFetchedCompliancePurposesRef.current = false;
      currentAssessmentIdRef.current = assessmentId;
    }

    // Check if compliance purposes was cleared (e.g., after objective deletion in Tab 2)
    // Only treat as "cleared" if we previously fetched successfully and are NOT currently fetching
    const wasCleared = compliancePurposes.length === 0
      && hasFetchedCompliancePurposesRef.current
      && !isFetchingCompliancePurposesRef.current;
    if (wasCleared) {
      hasFetchedCompliancePurposesRef.current = false;
    }

    // Skip if already fetched for this assessment
    if (hasFetchedCompliancePurposesRef.current) {
      return;
    }

    // Mark as fetched and fetching to prevent duplicates
    hasFetchedCompliancePurposesRef.current = true;
    isFetchingCompliancePurposesRef.current = true;

    fetchCompliancePurposes().finally(() => {
      isFetchingCompliancePurposesRef.current = false;
    });
  }, [assessmentId, currentAssessmentDetails, compliancePurposes.length, fetchCompliancePurposes]);

  // ==================== FRAUD TRIANGLE HANDLERS ====================

  // Handle card click to enter edit mode
  const handleCardClick = (cardType: FraudCardType) => {
    if (editingCard !== cardType) {
      setEditingCard(cardType);
      setTempText(fraudTriangle[cardType]);
      
      // Set cursor to end of text after a brief delay to ensure textarea is rendered
      setTimeout(() => {
        if (textareaRef.current && textareaRef.current.resizableTextArea) {
          const textarea = textareaRef.current.resizableTextArea.textArea;
          textarea.focus();
            // Set cursor to end of text
          textarea.setSelectionRange(textarea.value.length, textarea.value.length);
        }
      }, 0);
    }
  };

  // Handle blur to save and exit edit mode with optimistic update
  const handleBlur = async (cardType: FraudCardType) => {
    if (!currentAssessmentDetails || !assessmentId) {
      setEditingCard(null);
      return;
    }

    // Save previous state for rollback in case of error
    const previousAssessmentDetails = { ...currentAssessmentDetails };

    // Optimistic update: Update Redux state immediately
    const updatedDetails = {
      ...currentAssessmentDetails,
      [cardType]: tempText,
    };
    dispatch(setAssessmentDetails(updatedDetails));

    // Exit edit mode immediately for better UX
    setEditingCard(null);

    try {
      // console.log(`📤 Autosaving ${cardType}:`, tempText);
      
      // Call appropriate API based on card type
      switch (cardType) {
        case 'incentives':
          await updateIncentives({
            assessmentId,
            content: tempText,
          });
          break;
        case 'opportunities':
          await updateOpportunities({
            assessmentId,
            content: tempText,
          });
          break;
        case 'rationalizations':
          await updateRationalizations({
            assessmentId,
            content: tempText,
          });
          break;
      }

      // console.log(`✅ ${cardType} saved to backend`);
      // Success: State already updated optimistically, no need to refetch
      
    } catch (error: any) {
      console.error('❌ Failed to autosave:', error);
      
      // Rollback: Restore previous state on error
      dispatch(setAssessmentDetails(previousAssessmentDetails));
      
      // Restore edit mode so user can try again
      setEditingCard(cardType);
      setTempText(previousAssessmentDetails[cardType] || '');
      
      message.error(error?.message || `Failed to save ${cardType}`);
    }
  };

  // ==================== COMPLIANCE PURPOSE HANDLERS ====================

  const handleAddItem = () => {
    setDrawerMode('add');
    setSelectedItemForEdit(null);
    setInsertItemAtIndex(null);
    setIsDrawerOpen(true);
  };

  const handleAddItemAbove = (item: CompliancePurpose) => {
    const itemIndex = compliancePurposes.findIndex(cp => cp.id === item.id);
    setDrawerMode('add');
    setSelectedItemForEdit(null);
    setInsertItemAtIndex(itemIndex);
    setIsDrawerOpen(true);
  };

  const handleAddItemBelow = (item: CompliancePurpose) => {
    const itemIndex = compliancePurposes.findIndex(cp => cp.id === item.id);
    setDrawerMode('add');
    setSelectedItemForEdit(null);
    setInsertItemAtIndex(itemIndex + 1);
    setIsDrawerOpen(true);
  };

  const handleEditItem = (item: CompliancePurpose) => {
    setDrawerMode('edit');
    setSelectedItemForEdit(item);
    setIsDrawerOpen(true);
  };

  const handleDeleteItem = (item: CompliancePurpose) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const handleDeleteItemConfirm = async () => {
    if (!itemToDelete) return;

    setIsDeletingItem(true);
    try {
      // console.log('📤 Deleting compliance purpose...');
      await deleteCompliancePurpose(itemToDelete.id);
      
      // Refetch to ensure data consistency (silent - no loading)
      await fetchCompliancePurposes(true);

      message.success('Fraud risk factor deleted successfully');
      setShowDeleteModal(false);
      setItemToDelete(null);
    } catch (error: any) {
      console.error('❌ Failed to delete compliance purpose:', error);
      message.error(error?.message || 'Failed to delete fraud risk factor');
    } finally {
      setIsDeletingItem(false);
    }
  };

  const handleDeleteItemCancel = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setSelectedItemForEdit(null);
    setInsertItemAtIndex(null);
  };

  const handleDrawerSubmit = async (formData: CompliancePurposeFormData) => {
    if (!assessmentId) {
      message.error('Assessment ID is missing');
      return;
    }

    try {
      if (drawerMode === 'add') {
        // Determine target index
        const targetIndex = insertItemAtIndex !== null ? insertItemAtIndex : compliancePurposes.length;
        
        // console.log('📤 Creating compliance purpose...');
        await createCompliancePurpose({
          assessmentId,
          number: targetIndex + 1,
          description: formData.specificFraudRiskFactors,
          objectiveIds: formData.objectives,
          controlIds: formData.fraudRiskMitigatingControls,
        });

        message.success('Fraud risk factor added successfully');

        // Refetch to get correct IDs and order from backend (silent - no loading)
        await fetchCompliancePurposes(true);
      } else {
        if (!selectedItemForEdit) {
          message.error('Item not found');
          return;
        }

        // console.log('📤 Updating compliance purpose...');
        await updateCompliancePurpose({
          assessmentId,
          description: formData.specificFraudRiskFactors,
          objectiveIds: formData.objectives,
          controlIds: formData.fraudRiskMitigatingControls,
          compliancePurposeId: selectedItemForEdit.id,
        });

        await fetchCompliancePurposes(true);
        message.success('Fraud risk factor updated successfully');
      }
    } catch (error: any) {
      console.error('❌ Failed to save compliance purpose:', error);
      const defaultErrorMessage = `Failed to ${drawerMode === 'add' ? 'add' : 'update'} fraud risk factor`;
      message.error(error?.message || defaultErrorMessage);
      throw error;
    }
  };

  // ==================== FRAUD RISK CONCLUSION HANDLERS ====================

  const handleEditConclusion = () => {
    setIsConclusionDrawerOpen(true);
  };

  const handleConclusionDrawerClose = () => {
    setIsConclusionDrawerOpen(false);
  };

  const handleConclusionSubmit = async (formData: QualitativeFactorFormData) => {
    try {
      // Update Redux - this will sync with Tab 1
      if (currentAssessmentDetails) {
        await updateFraudRisk({
          assessmentId: currentAssessmentDetails.assessmentId,
          riskRanking: denormalizeRiskRanking(formData.riskRanking),
          description: formData.customComment,
        });
        
        // Refetch details to update Redux
        await refetchDetails(true);
        message.success('Fraud Risk Conclusion updated successfully');
      }
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Failed to update Fraud Risk Conclusion');
      throw error;
    }
  };

  // Handle inline risk ranking change
  const handleConclusionRiskRankingChange = async (newRanking: RiskRanking) => {
    if (currentAssessmentDetails) {
      dispatch(setAssessmentDetails({
        ...currentAssessmentDetails,
        fraudRiskRanking: newRanking,
      }));
      await updateFraudRisk({
        assessmentId: currentAssessmentDetails.assessmentId,
        riskRanking: denormalizeRiskRanking(newRanking),
        description: fraudRiskConclusion.customComment,
      });
      // Refetch details to make sure the data is synced
      await refetchDetails(true);
    }
  };

  // ==================== COLUMN TEMPLATES ====================

  const indexTemplate = (props: CompliancePurpose) => {
    return <IndexCell>{props.index.toString().padStart(2, '0')}</IndexCell>;
  };

  const factorsTemplate = (props: CompliancePurpose) => {
    return <div>{props.specificFraudRiskFactors}</div>;
  };

  const objectivesTemplate = (props: CompliancePurpose) => {
    if (!props.objectiveNumbers || props.objectiveNumbers.length === 0) {
      return <span style={{ color: '#9CA3AF' }}>-</span>;
    }

    // Group objectives by process code
    const grouped: Record<string, string[]> = {};
    props.objectiveNumbers.forEach((num, idx) => {
      const processCode = props.objectiveProcessCodes?.[idx] || '?';
      if (!grouped[processCode]) grouped[processCode] = [];
      grouped[processCode].push(num);
    });

    const groupEntries = Object.entries(grouped);

    const renderChips = () => groupEntries.map(([processCode, numbers]) => {
      const matchingRa = riskAssessmentCycles
        .flatMap(c => c.riskAssessments)
        .find(ra => ra.processCode === processCode);
      const chipAssessmentId = matchingRa?.id;

      return (
        <ObjectiveGroupTag
          key={processCode}
          onClick={(e) => {
            e.stopPropagation();
            if (chipAssessmentId) {
              navigate(`/risk-assessment/${chipAssessmentId}/tab2`, {
                replace: true,
              });
            }
          }}
          title={chipAssessmentId ? `Click to view objectives for ${processCode}` : processCode}
        >
          <ObjectiveGroupProcessCode>{processCode}</ObjectiveGroupProcessCode>
          <ObjectiveGroupNumbers>
            {[...numbers]
              .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
              .map((num, i) => (
                <ObjectiveGroupNumberCircle key={i}>{num}</ObjectiveGroupNumberCircle>
              ))}
          </ObjectiveGroupNumbers>
        </ObjectiveGroupTag>
      );
    });

    const tooltipContent = (
      <div>
        <TooltipTitle>Objectives</TooltipTitle>
        <TooltipList>
          {groupEntries.map(([processCode, numbers]) => {
            const sorted = [...numbers].sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
            const matchingRa = riskAssessmentCycles
              .flatMap(c => c.riskAssessments)
              .find(ra => ra.processCode === processCode);
            const chipAssessmentId = matchingRa?.id;
            return (
              <TooltipItem
                key={processCode}
                onClick={(e) => {
                  e.stopPropagation();
                  if (chipAssessmentId) {
                    navigate(`/risk-assessment/${chipAssessmentId}/tab2`, {
                      replace: true,
                    });
                  }
                }}
                style={{ cursor: chipAssessmentId ? 'pointer' : 'default' }}
              >
                {processCode}: {sorted.join(', ')}
              </TooltipItem>
            );
          })}
        </TooltipList>
      </div>
    );

    return (
      <Tooltip
        title={tooltipContent}
        placement="top"
        overlayStyle={{ maxWidth: '400px' }}
        overlayInnerStyle={{
          backgroundColor: '#FFFFFF',
          padding: '16px',
          borderRadius: '12px',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
        }}
        color="#FFFFFF"
      >
        <ObjectivesCell>
          {renderChips()}
        </ObjectivesCell>
      </Tooltip>
    );
  };

  const handleNavigateToControl = (controlId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/control-activities/${controlId}`);
  };

  const controlsTemplate = (props: CompliancePurpose) => {
    // Use controlCodes for display (e.g., ['A01.03', 'A01.10'])
    if (!props.controlCodes || props.controlCodes.length === 0) {
      return <span style={{ color: '#9CA3AF' }}>-</span>;
    }

    const controlsContent = (
      <div>
        {props.controlCodes.map((code, index) => {
          const controlId = props.fraudRiskMitigatingControls?.[index];
          return (
            <ControlChip
              key={index}
              $clickable={!!controlId}
              onClick={(e) => controlId && handleNavigateToControl(controlId, e)}
              title={controlId ? 'Click to view control details' : undefined}
            >
              {code}
            </ControlChip>
          );
        })}
      </div>
    );

    // Show tooltip only if multiple controls
    if (props.controlCodes.length > 1) {
      const tooltipContent = (
        <div>
          <TooltipTitle>Fraud Risk Mitigating Controls</TooltipTitle>
          <TooltipList>
            {props.controlCodes.map((code, index) => {
              const controlId = props.fraudRiskMitigatingControls?.[index];
              return (
                <TooltipItem
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (controlId) handleNavigateToControl(controlId, e);
                  }}
                  style={{ cursor: controlId ? 'pointer' : 'default' }}
                >
                  {code}
                </TooltipItem>
              );
            })}
          </TooltipList>
        </div>
      );

      return (
        <Tooltip
          title={tooltipContent}
          placement="top"
          overlayStyle={{ maxWidth: '400px' }}
          overlayInnerStyle={{
            backgroundColor: '#FFFFFF',
            padding: '16px',
            borderRadius: '12px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
          }}
          color="#FFFFFF"
        >
          {controlsContent}
        </Tooltip>
      );
    }

    return controlsContent;
  };

  const actionTemplate = (props: CompliancePurpose) => {
    const getMenuItems = (): MenuProps['items'] => [
      {
        key: 'add-above',
        label: (
          <MenuItemContent>
            <ArrowUpIcon />
            <MenuItemText>Add row above</MenuItemText>
          </MenuItemContent>
        ),
        onClick: () => handleAddItemAbove(props),
      },
      {
        key: 'add-below',
        label: (
          <MenuItemContent>
            <ArrowDownIcon />
            <MenuItemText>Add row below</MenuItemText>
          </MenuItemContent>
        ),
        onClick: () => handleAddItemBelow(props),
      },
      {
        key: 'edit',
        label: (
          <MenuItemContent>
            <EditIcon />
            <MenuItemText>Edit</MenuItemText>
          </MenuItemContent>
        ),
        onClick: () => handleEditItem(props),
      },
      {
        key: 'delete',
        label: (
          <MenuItemContent>
            <TrashIcon />
            <MenuItemText $danger>Delete row</MenuItemText>
          </MenuItemContent>
        ),
        onClick: () => handleDeleteItem(props),
      },
    ];

    const handleActionButtonClick = (e: React.MouseEvent) => {
      e.stopPropagation();
    };

    return (
      <div onClick={handleActionButtonClick}>
        <Dropdown
          menu={{ items: getMenuItems() }}
          trigger={['click']}
          placement="bottomRight"
        >
          <ActionButton>
            <MoreOutlined />
          </ActionButton>
        </Dropdown>
      </div>
    );
  };

  // ==================== RENDER HELPERS ====================

  // Render Fraud Risk Conclusion Row (sync with Tab 1 - Fraud Risk fixed row)
  const renderFraudRiskConclusionRow = () => {
    const getRiskMenuItems = (): MenuProps['items'] => [
      { key: 'High', label: 'High', onClick: () => handleConclusionRiskRankingChange('High') },
      { key: 'Med', label: 'Med', onClick: () => handleConclusionRiskRankingChange('Med') },
      { key: 'Low', label: 'Low', onClick: () => handleConclusionRiskRankingChange('Low') },
      { key: 'Unranked', label: 'Unranked', onClick: () => handleConclusionRiskRankingChange('Unranked') },
    ];

    const getActionMenuItems = (): MenuProps['items'] => [
      {
        key: 'add-above',
        label: (
          <MenuItemContent>
            <ArrowUpIcon />
            <MenuItemText>Add row above</MenuItemText>
          </MenuItemContent>
        ),
        onClick: handleAddItem,
      },
      {
        key: 'edit',
        label: (
          <MenuItemContent>
            <EditIcon />
            <MenuItemText>Edit Item</MenuItemText>
          </MenuItemContent>
        ),
        onClick: handleEditConclusion,
      },
    ];

    const handleActionButtonClick = (e: React.MouseEvent) => {
      e.stopPropagation();
    };

    return (
      <FraudRiskConclusionRow>
        <div />
        <FraudRiskConclusionCell>
          <ConclusionLabel>Fraud Risk Conclusion</ConclusionLabel>
        </FraudRiskConclusionCell>
        <FraudRiskConclusionCell style={{ textAlign: 'left' }}>
          {isAdmin ? (
            <Dropdown
              menu={{ items: getRiskMenuItems() }}
              trigger={['click']}
              placement="bottomRight"
            >
              <RiskChip $risk={fraudRiskConclusion.riskRanking}>
                {fraudRiskConclusion.riskRanking}
              </RiskChip>
            </Dropdown>
          ) : (
            <RiskChip $risk={fraudRiskConclusion.riskRanking} style={{ cursor: 'default' }}>
              {fraudRiskConclusion.riskRanking}
            </RiskChip>
          )}
        </FraudRiskConclusionCell>
        <FraudRiskConclusionCell>
          <ConclusionDescription>{fraudRiskConclusion.customComment}</ConclusionDescription>
        </FraudRiskConclusionCell>
        {isAdmin && (
          <FraudRiskConclusionCell style={{ justifyContent: 'center' }} onClick={handleActionButtonClick}>
            <Dropdown
              menu={{ items: getActionMenuItems() }}
              trigger={['click']}
              placement="bottomLeft"
            >
              <ActionButton>
                <MoreOutlined />
              </ActionButton>
            </Dropdown>
          </FraudRiskConclusionCell>
        )}
      </FraudRiskConclusionRow>
    );
  };

  // Render individual fraud card
  const renderFraudCard = (
    cardType: FraudCardType,
    title: string,
    disabled: boolean
  ) => {
    const isEditing = editingCard === cardType;
    const content = fraudTriangle[cardType];

    return (
      <FraudCard
        $isEditing={isEditing}
        onClick={() => !isEditing && !disabled && handleCardClick(cardType)}
      >
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardBody>
          {isEditing && !disabled ? (
            <StyledTextArea
              ref={textareaRef}
              value={tempText}
              onChange={(e) => setTempText(e.target.value)}
              onBlur={() => handleBlur(cardType)}
              placeholder={`Enter ${title.toLowerCase()}...`}
              autoSize={{ minRows: 6, maxRows: 12 }}
              autoFocus
            />
          ) : (
            <ReadOnlyText $disabled={disabled}>{content}</ReadOnlyText>
          )}
        </CardBody>
      </FraudCard>
    );
  };

  // Render Compliance Purpose section
  // Always shows Fraud Risk Conclusion Row (fixed row), no empty state needed
  const renderCompliancePurpose = () => {
    // Loading state - only show when BOTH details and compliance purposes are loading
    if (isLoadingDetails && isLoadingCompliancePurposes) {
      return (
        <CompliancePurposeContent>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100px',
            backgroundColor: 'white',
          }}>
            <LoadingSpinner>
              <div className="spinner" />
            </LoadingSpinner>
          </div>
        </CompliancePurposeContent>
      );
    }

    // Always show table (may be empty) + Fraud Risk Conclusion Row (fixed row)
    return (
      <CompliancePurposeContent>
        <GridWrapper>
          {/* Only render grid if there are compliance purposes */}
          {compliancePurposes.length > 0 && (
            <GridComponent
              key={`compliance-purposes-grid-${compliancePurposes.length}`}
              dataSource={compliancePurposes}
              allowPaging={false}
              enableHover={true}
            >
              <ColumnsDirective>
                <ColumnDirective
                  field='index'
                  headerText='#'
                  width='68'
                  textAlign='Center'
                  template={indexTemplate}
                />
                <ColumnDirective
                  field='specificFraudRiskFactors'
                  headerText='Specific Fraud Risk Factors'
                  textAlign='Left'
                  template={factorsTemplate}
                />
                <ColumnDirective
                  field='objectiveNumbers'
                  headerText='Objectives'
                  width='240'
                  textAlign='Left'
                  template={objectivesTemplate}
                />
                <ColumnDirective
                  field='controlCodes'
                  headerText='Fraud Risk Mitigating Controls'
                  width={isAdmin ? '300' : '380'}
                  textAlign='Left'
                  template={controlsTemplate}
                />
                <ColumnDirective
                  headerText='Action'
                  width='80'
                  textAlign='Center'
                  template={actionTemplate}
                  visible={isAdmin}
                />
              </ColumnsDirective>
              <Inject services={[Page]} />
            </GridComponent>
          )}
          
          {/* Fraud Risk Conclusion Row - ALWAYS visible (fixed row, synced with Tab 1) */}
          {renderFraudRiskConclusionRow()}
        </GridWrapper>
      </CompliancePurposeContent>
    );
  };

  return (
    <>
    <TabContent>
        {/* Fraud Triangle Section */}
        <SectionTitle>Specific Fraud Risks Assessment</SectionTitle>
        
        <FraudTriangleGrid>
          {renderFraudCard('incentives', 'Incentives & Pressures', !isAdmin)}
          {renderFraudCard('opportunities', 'Opportunities', !isAdmin)}
          {renderFraudCard('rationalizations', 'Rationalizations & Attitude', !isAdmin)}
        </FraudTriangleGrid>

        {/* Compliance Purpose Section */}
        <CompliancePurposeSection>
          <CompliancePurposeTitleRow>
            <CompliancePurposeTitle>Compliance Purpose</CompliancePurposeTitle>
          </CompliancePurposeTitleRow>
          <CompliancePurposeDescription>
            The organization considers potential for fraud in assessing risks to the achievement of objectives
          </CompliancePurposeDescription>
          
          {renderCompliancePurpose()}
        </CompliancePurposeSection>
    </TabContent>

      {/* Compliance Purpose Item Drawer */}
      <CompliancePurposeItemDrawer
        open={isDrawerOpen}
        mode={drawerMode}
        initialValues={selectedItemForEdit ? {
          id: selectedItemForEdit.id,
          fraudRiskNumber: String(selectedItemForEdit.index).padStart(2, '0'),
          specificFraudRiskFactors: selectedItemForEdit.specificFraudRiskFactors,
          objectives: selectedItemForEdit.objectives,
          objectiveNumbers: selectedItemForEdit.objectiveNumbers,
          objectiveProcessCodes: selectedItemForEdit.objectiveProcessCodes,
          fraudRiskMitigatingControls: selectedItemForEdit.fraudRiskMitigatingControls,
        } : undefined}
        onClose={handleDrawerClose}
        onSubmit={handleDrawerSubmit}
      />

      {/* Fraud Risk Conclusion Drawer (sync with Tab 1 - Fraud Risk) */}
      <QualitativeItemDrawer
        open={isConclusionDrawerOpen}
        mode='edit'
        initialValues={{
          id: fraudRiskConclusion.id,
          qualitativeFactor: 'Fraud Risk Conclusion',
          riskRanking: fraudRiskConclusion.riskRanking,
          customComment: fraudRiskConclusion.customComment,
          isFixed: true,
        }}
        onClose={handleConclusionDrawerClose}
        onSubmit={handleConclusionSubmit}
      />

      {/* Delete Fraud Risk Factor Confirmation Modal */}
      <ConfirmModal
        open={showDeleteModal}
        onClose={handleDeleteItemCancel}
        onConfirm={handleDeleteItemConfirm}
        icon={warningIcon}
        title="Delete Fraud Risk Factor"
        description="Are you sure you want to delete this fraud risk factor? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        danger={true}
        loading={isDeletingItem}
      />

    </>
  );
};

export default Tab3FraudRisk;
